import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SOPExpandableTable from "../SOPTable/SOPExpandableTable";
import { useTranslation } from "react-i18next";
import Loader from "react-spinner-loader";
import { toast } from "react-toastify";
import { getData, deleteDataByQueryIds } from "../../../../Services/Services";
import { getThemeURL } from "../../../../Services/EndPoints";
import * as EndPoints from "../../../../Services/EndPoints";
import { Deleteicon, Editicon } from "../../../../Components/IconComponents";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { RoleContext } from "../../../../index";
import { Eye, Grid3x3, RefreshCw, Sheet, FileText, History as HistoryIcon } from "lucide-react";
import ResourceAuditHistoryModal from "./ResourceAuditHistoryModal";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const EXPORT_COLUMNS = [
  "Source ID",
  "Incident Category",
  "Severity",
  "Created On",
  "Resource Name",
  "Available Qty",
  "Damaged Qty",
  "Deployed Qty",
  "Total Qty",
  "Qty To Deploy",
];

const exportFileStamp = () => {
  const p = (n) => String(n).padStart(2, "0");
  const n = new Date();
  return `${p(n.getDate())}-${p(n.getMonth() + 1)}-${n.getFullYear()}_${p(n.getHours())}${p(
    n.getMinutes()
  )}${p(n.getSeconds())}`;
};

const MATRIX_HELP_TEXT =
  "Create a resource matrix by selecting the Incident Category and Severity, then map the required resources before saving";

const EMPTY_FILTERS = { category: "", severity: "", from: "", to: "" };

const filterFieldWrap = { display: "flex", flexDirection: "column", gap: "4px" };
// Local (not UTC) today as YYYY-MM-DD — the UTC date is a day behind for part of the day in IST.
const TODAY_STR = (() => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
})();
// Typing a future date into a native date input bypasses `max`, so clamp it here.
const clampToToday = (v) => (v && v > TODAY_STR ? TODAY_STR : v);

const filterLabelStyle = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: ".4px",
  textTransform: "uppercase",
  color: "#5b6b7a",
};
const filterControlStyle = {
  height: "34px",
  lineHeight: "32px",
  fontSize: "12px",
  fontFamily: "inherit",
  padding: "0 8px",
  border: "1px solid #d5e0ea",
  borderRadius: "6px",
  color: "#1a2e35",
  background: "#fff",
  outline: "none",
  minWidth: "160px",
  boxSizing: "border-box",
};
const filterBtnBase = {
  height: "34px",
  fontSize: "12px",
  fontWeight: 600,
  padding: "0 16px",
  borderRadius: "6px",
  cursor: "pointer",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
};
const applyFilterBtnStyle = {
  ...filterBtnBase,
  border: "1px solid #2E6DA4",
  color: "#fff",
  background: "#2E6DA4",
};
const clearFilterBtnActiveStyle = {
  ...filterBtnBase,
  border: "1px solid #c0392b",
  color: "#c0392b",
  background: "#fff",
};
const clearFilterBtnDisabledStyle = {
  ...filterBtnBase,
  border: "1px solid #e5e9ef",
  color: "#aab4bf",
  background: "#f4f6f8",
  cursor: "not-allowed",
};
const refreshFilterBtnStyle = {
  ...filterBtnBase,
  width: "34px",
  padding: 0,
  border: "1px solid #d5e0ea",
  color: "#2E6DA4",
  background: "#fff",
};
const excelExportBtnStyle = {
  ...filterBtnBase,
  width: "34px",
  padding: 0,
  border: "1px solid #10B981",
  color: "#fff",
  background: "#10B981",
};
const pdfExportBtnStyle = {
  ...filterBtnBase,
  width: "34px",
  padding: 0,
  border: "1px solid #dc3545",
  color: "#fff",
  background: "#dc3545",
};
const exportBtnDisabledStyle = {
  ...filterBtnBase,
  width: "34px",
  padding: 0,
  border: "1px solid #e5e9ef",
  color: "#aab4bf",
  background: "#f4f6f8",
  cursor: "not-allowed",
};

const badgeBase = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.6,
};
const CATEGORY_BADGE_PALETTE = [
  "#1d5fa8", // blue
  "#1e7e34", // green
  "#c2410c", // orange
  "#6b3fa0", // purple
  "#b02a4a", // rose
  "#0f766e", // teal
  "#a67c00", // gold
  "#475569", // slate
  "#0e7490", // cyan
  "#9d174d", // magenta
];
// Stable colours for common hazard types; anything else is hashed to a palette slot.
const KNOWN_CATEGORY_COLORS = {
  earthquake: 7,
  flood: 0,
  "flash flood": 0,
  "cloud burst": 5,
  cloudburst: 5,
  avalanche: 8,
  landslide: 2,
  cyclone: 3,
  storm: 3,
  fire: 4,
  wildfire: 4,
  drought: 6,
  "heat wave": 2,
  "cold wave": 8,
  tsunami: 1,
  "industrial hazard": 9,
};
const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};
const getCategoryBadgeStyle = (category) => {
  const key = (category || "").trim().toLowerCase();
  const idx = Object.prototype.hasOwnProperty.call(KNOWN_CATEGORY_COLORS, key)
    ? KNOWN_CATEGORY_COLORS[key] % CATEGORY_BADGE_PALETTE.length
    : hashString(key) % CATEGORY_BADGE_PALETTE.length;
  return {
    ...badgeBase,
    background: CATEGORY_BADGE_PALETTE[idx],
    color: "#fff",
    border: "1px solid transparent",
  };
};
const getSeverityBadgeStyle = (severity) => {
  const s = (severity || "").toLowerCase();
  let palette = { background: "#eef2f7", color: "#5b6b7a", borderColor: "#d5e0ea" };
  if (s.includes("minor") || s.includes("level 1")) {
    palette = { background: "#e6f4ea", color: "#1e7e34", borderColor: "#b7e1c1" };
  } else if (s.includes("moderate") || s.includes("level 2")) {
    palette = { background: "#fff4e0", color: "#b26a00", borderColor: "#f2d9a8" };
  } else if (s.includes("major") || s.includes("level 3")) {
    palette = { background: "#ffe9d9", color: "#c2410c", borderColor: "#f3c2a0" };
  } else if (s.includes("critical") || s.includes("level 4")) {
    palette = { background: "#fde5e5", color: "#c0392b", borderColor: "#f2b8b8" };
  }
  return {
    ...badgeBase,
    background: palette.background,
    color: palette.color,
    border: `1px solid ${palette.borderColor}`,
  };
};


const isTruthyFlag = (v) => v === true || v === 1 || v === "true" || v === "1";

const formatDateTime12 = (value) => {
  if (!value) return "NA";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return "NA";
  const p = (n) => String(n).padStart(2, "0");
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(h)}:${p(d.getMinutes())} ${ampm}`;
};

const toDateOnly = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const IncidentCategoryMatrix = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleName } = useContext(RoleContext);
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [stateId, setStateId] = useState(null);

  const [historyRow, setHistoryRow] = useState(null);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    const fetchStateId = async () => {
      try {
        const profile = await getData(getThemeURL);
        setStateId(profile[0]?.state_id || null);
      } catch (e) {
        console.error("Failed to fetch stateId:", e);
      }
    };
    fetchStateId();
  }, []);

  useEffect(() => {
    if (stateId === null) return;
    fetchMatrixList();
  },
// eslint-disable-next-line react-hooks/exhaustive-deps
  [stateId]);

  // Maps one grouped record (as returned by both the active and deleted
  // matrix endpoints — { hazardType, severityLevel, groupSeqId, resources: [...],
  // latestCreatedBy/On, latestUpdatedBy/On }) into the row shape the table
  // (and the edit/view form) expects. `forcedIsDeleted` reflects which
  // endpoint the group came from, since Active/Inactive are now separate
  // API calls rather than a client-side split of one combined list.
  const mapMatrixGroup = (item, forcedIsDeleted) => {
    const rawResources = (item.resources || []).map((r) => ({
      id: r.id,
      src_seq_id: r.src_seq_id || item.groupSeqId,
      resource_id: r.resource_id,
      resource_name: r.resource_name,
      required_quantity: r.required_quantity,
      available_quantity: r.available_quantity,
      cum_deployed_quantity: r.cum_deployed_quantity,
      cum_damaged_quantity: r.cum_damaged_quantity,
      cum_available_quantity: r.cum_available_quantity,
      cum_total_quantity: r.cum_total_quantity,
      is_deleted: isTruthyFlag(r.is_deleted),
      createdByName: r.createdByName,
      updatedByName: r.updatedByName,
      updated_on: r.updated_on,
    }));

    return {
      id: rawResources[0]?.id ?? null,
      src_seq_id: item.groupSeqId,
      incident_category: item.hazardType,
      severity: item.severityLevel,
      resources: rawResources,
      rawResources,
      hazardType: item.hazardType,
      severityLevel: item.severityLevel,
      isDeleted: !!forcedIsDeleted,
      isRunning: isTruthyFlag(item.isRunning),
      created_by_name: item.latestCreatedByName || rawResources[0]?.createdByName || "NA",
      updated_by_name: item.latestUpdatedByName || "NA",
      updated_on_raw: item.latestUpdatedOn || null,
      updated_on: formatDateTime12(item.latestUpdatedOn),
      created_on_raw: item.latestCreatedOn || null,
      created_on: formatDateTime12(item.latestCreatedOn),
    };
  };

  const fetchMatrixList = async () => {
    setLoader(true);
    try {
      const [activeResp, deletedResp] = await Promise.all([
        getData(EndPoints.getActiveCategoryMatrixList.replace("STATEID", stateId)),
        getData(EndPoints.getDeletedCategoryMatrixList.replace("STATEID", stateId)),
      ]);

      const activeRows = Array.isArray(activeResp)
        ? activeResp.map((item) => mapMatrixGroup(item, false))
        : [];
      const deletedRows = Array.isArray(deletedResp)
        ? deletedResp.map((item) => mapMatrixGroup(item, true))
        : [];

      setData([...activeRows, ...deletedRows]);
    } catch (error) {
      console.error("Error fetching matrix list:", error);
    } finally {
      setLoader(false);
    }
  };

  const addNewHandler = () => {
    navigate("create");
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Source ID",
        accessor: "src_seq_id",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: t("categorymatrixformlabels.incidentcategory"),
        accessor: "incident_category",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? <span style={getCategoryBadgeStyle(value)}>{value}</span> : "NA",
      },
      {
        Header: t("categorymatrixformlabels.severity"),
        accessor: "severity",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? <span style={getSeverityBadgeStyle(value)}>{value}</span> : "NA",
      },
      {
        Header: t("taskmanagementtableheaders.Createdon"),
        accessor: "created_on",
        disableSortBy: true,
      },
      {
        Header: "Updated On",
        accessor: "updated_on",
        disableSortBy: true,
      },
      {
        Header: t("taskmanagementtableheaders.action"),
        disableSortBy: true,
        Cell: (props) => {
          const [isDelete, setIsDelete] = useState(false);
          const toggleDelete = () => setIsDelete(!isDelete);
          // Deleted (inactive) rows are shown in their own table with view /
          // audit-history only — no edit or delete.
          const rowDeleted = props.row.original.isDeleted === true;
          const isRunning = props.row.original.isRunning === true;
          const runningTitle = "This action is disabled while the matrix is Running.";

          const handleDeleteRow = async () => {
            try {
              setLoader(true);
              const ids = (props.row.original.rawResources || []).map((r) => r.id);
              const res = await deleteDataByQueryIds(EndPoints.deleteCategoryMatrix, ids);
              if (res && (res.status === "success" || res.status === "multi-status")) {
                toast.success(res.msg || "Matrix deleted successfully.", { position: "top-center" });
                fetchMatrixList();
                setIsDelete(false);
              } else {
                toast.error(res.msg || "Error deleting", { position: "top-center" });
              }
            } catch (error) {
              console.error("Error deleting matrix entry:", error);
              toast.error("Error deleting entry", { position: "top-center" });
            } finally {
              setLoader(false);
            }
          };

          return (
            <div className="usertable__actionsicon" style={{ display: "flex", alignItems: "center" }}>
              <span
                className="cursor-pointer me-2 icm-keep-active"
                title="View"
                onClick={() => navigate("create", { state: { editData: props.row.original, viewMode: true } })}
              >
                <Eye size={14} title="View" />
              </span>
              <span
                className="cursor-pointer me-2 icm-keep-active"
                title="Audit History"
                onClick={() => setHistoryRow(props.row.original)}
              >
                <HistoryIcon size={14} title="Audit History" />
              </span>
              {roleName === "SEOC" && !rowDeleted && (
                <span
                  className={isRunning ? "me-2" : "cursor-pointer me-2"}
                  title={isRunning ? runningTitle : "Edit"}
                  onClick={() => !isRunning && navigate("create", { state: { editData: props.row.original } })}
                  style={isRunning ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <Editicon title="Edit" />
                </span>
              )}
              {roleName === "SEOC" && !rowDeleted && (
                <span
                  onClick={() => !isRunning && toggleDelete()}
                  title={isRunning ? runningTitle : "Delete"}
                  className={isRunning ? "" : "cursor-pointer"}
                  style={isRunning ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <Deleteicon
                    className="cursor-pointer"
                    title="Delete"
                  />
                </span>
              )}
              {isDelete && (
                <Confirmpopup
                  show={isDelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={toggleDelete}
                  row={props.row}
                  deleteRow={handleDeleteRow}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, stateId]
  );

  const categoryOptions = useMemo(
    () => [...new Set(data.map((d) => d.incident_category).filter(Boolean))].sort(),
    [data]
  );
  const severityOptions = useMemo(
    () => [...new Set(data.map((d) => d.severity).filter(Boolean))].sort(),
    [data]
  );
  const filteredData = useMemo(() => {
    const from = appliedFilters.from ? new Date(`${appliedFilters.from}T00:00:00`) : null;
    const to = appliedFilters.to ? new Date(`${appliedFilters.to}T00:00:00`) : null;
    return data.filter((row) => {
      if (appliedFilters.category && row.incident_category !== appliedFilters.category) return false;
      if (appliedFilters.severity && row.severity !== appliedFilters.severity) return false;
      if (from || to) {
        const rowDate = toDateOnly(row.created_on_raw);
        if (!rowDate) return false;
        if (from && rowDate < from) return false;
        if (to && rowDate > to) return false;
      }
      return true;
    });
  }, [data, appliedFilters]);

  const activeData = useMemo(
    () => filteredData.filter((row) => !row.isDeleted),
    [filteredData]
  );
  const inactiveData = useMemo(
    () => filteredData.filter((row) => row.isDeleted),
    [filteredData]
  );

  const applyFilters = () => {
    setAppliedFilters({
      category: categoryFilter,
      severity: severityFilter,
      from: dateFrom,
      to: dateTo,
    });
  };

  // Incident Category / Severity filter instantly on selection — no need to
  // hit Apply for these two. The date range still requires Apply, so only
  // the changed field is merged into the already-applied filters here.
  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    setAppliedFilters((prev) => ({ ...prev, category: value }));
  };
  const handleSeverityFilterChange = (value) => {
    setSeverityFilter(value);
    setAppliedFilters((prev) => ({ ...prev, severity: value }));
  };

  const filtersActive =
    !!categoryFilter ||
    !!severityFilter ||
    !!dateFrom ||
    !!dateTo ||
    !!appliedFilters.category ||
    !!appliedFilters.severity ||
    !!appliedFilters.from ||
    !!appliedFilters.to;

  const clearFilters = () => {
    setCategoryFilter("");
    setSeverityFilter("");
    setDateFrom("");
    setDateTo("");
    setAppliedFilters(EMPTY_FILTERS);
  };

  const refreshList = () => {
    if (stateId !== null) fetchMatrixList();
  };

  // Flatten the (filtered) matrix + its expanded resource rows into export rows.
  const buildExportRows = () => {
    const rows = [];
    filteredData.forEach((item) => {
      const resources = item.rawResources || [];
      if (resources.length === 0) {
        rows.push({
          "Source ID": item.src_seq_id || "",
          "Incident Category": item.incident_category || "",
          Severity: item.severity || "",
          "Created On": item.created_on || "",
          "Resource Name": "No resources",
          "Available Qty": "",
          "Damaged Qty": "",
          "Deployed Qty": "",
          "Total Qty": "",
          "Qty To Deploy": "",
        });
        return;
      }
      resources.forEach((r) => {
        rows.push({
          "Source ID": item.src_seq_id || "",
          "Incident Category": item.incident_category || "",
          Severity: item.severity || "",
          "Created On": item.created_on || "",
          "Resource Name": r.resource_name || "NA",
          "Available Qty": r.cum_available_quantity ?? r.available_quantity ?? 0,
          "Damaged Qty": r.cum_damaged_quantity ?? 0,
          "Deployed Qty": r.cum_deployed_quantity ?? 0,
          "Total Qty": r.cum_total_quantity ?? 0,
          "Qty To Deploy": r.required_quantity ?? 0,
        });
      });
    });
    return rows;
  };

  const handleExportExcel = async () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Incident Category Matrix");

    const headerRow = ws.addRow(EXPORT_COLUMNS);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E6DA4" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    rows.forEach((r) => {
      const dataRow = ws.addRow(EXPORT_COLUMNS.map((col) => r[col]));
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    ws.columns.forEach((col, idx) => {
      col.width = idx <= 3 ? 22 : idx === 4 ? 34 : 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Incident_Category_Matrix_${exportFileStamp()}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }
    const doc = new jsPDF("l", "pt", "a4");
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(13);
    doc.text("Incident Category Matrix", pageWidth / 2, 40, { align: "center" });
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 56, {
      align: "center",
    });

    doc.autoTable({
      head: [EXPORT_COLUMNS],
      body: rows.map((r) => EXPORT_COLUMNS.map((c) => String(r[c] ?? ""))),
      startY: 70,
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
      headStyles: {
        fillColor: [46, 109, 164],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        fontSize: 7,
      },
      columnStyles: {
        0: { cellWidth: 78 },
        1: { cellWidth: 76 },
        2: { cellWidth: 84 },
        3: { cellWidth: 96 },
        4: { cellWidth: 150 },
        5: { cellWidth: 58, halign: "center" },
        6: { cellWidth: 58, halign: "center" },
        7: { cellWidth: 58, halign: "center" },
        8: { cellWidth: 50, halign: "center" },
        9: { cellWidth: 62, halign: "center" },
      },
      margin: { left: 20, right: 20 },
    });

    doc.save(`Incident_Category_Matrix_${exportFileStamp()}.pdf`);
  };

  const renderFilterSelect = (label, value, onChange, options, allLabel) => (
    <div style={filterFieldWrap}>
      <label style={filterLabelStyle}>{label}</label>
      <select style={filterControlStyle} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );

  const filterBar = (
    <>
      <style>{`
        .icm-filter-bar input[type="date"] {
          position: relative;
          padding-right: 30px;
          display: inline-flex;
          align-items: center;
        }
        .icm-filter-bar input[type="date"]::-webkit-datetime-edit {
          display: inline-flex;
          align-items: center;
          padding: 0;
          line-height: normal;
        }
        .icm-filter-bar input[type="date"]::-webkit-datetime-edit-fields-wrapper {
          display: inline-flex;
          align-items: center;
        }
        .icm-filter-bar input[type="date"]::-webkit-calendar-picker-indicator {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          margin: 0;
          padding: 0;
          cursor: pointer;
          opacity: 0.55;
        }
        .icm-filter-bar input[type="date"]::-webkit-inner-spin-button,
        .icm-filter-bar input[type="date"]::-webkit-clear-button {
          display: none;
        }
        /* Tighter rows for this table */
        .datatable__container .sop-expandable-table > table > tbody > tr > td {
          padding-top: 5px;
          padding-bottom: 5px;
        }
      `}</style>
      <div
        className="icm-filter-bar"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "flex-end",
          padding: "10px 16px 14px",
          margin: "0 2px 14px",
          borderBottom: "1px solid #eef1f4",
        }}
      >
        {renderFilterSelect(
          "Incident Category",
          categoryFilter,
          handleCategoryFilterChange,
          categoryOptions,
          "All Categories"
        )}
        {renderFilterSelect(
          "Severity",
          severityFilter,
          handleSeverityFilterChange,
          severityOptions,
          "All Severities"
        )}
        <div style={filterFieldWrap}>
          <label style={filterLabelStyle}>Created From</label>
          <input
            type="date"
            style={filterControlStyle}
            value={dateFrom}
            max={dateTo && dateTo < TODAY_STR ? dateTo : TODAY_STR}
            onChange={(e) => setDateFrom(clampToToday(e.target.value))}
          />
        </div>
        <div style={filterFieldWrap}>
          <label style={filterLabelStyle}>Created To</label>
          <input
            type="date"
            style={filterControlStyle}
            value={dateTo}
            min={dateFrom || undefined}
            max={TODAY_STR}
            onChange={(e) => setDateTo(clampToToday(e.target.value))}
          />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
          <button type="button" style={applyFilterBtnStyle} onClick={applyFilters}>
            Apply
          </button>
          <button
            type="button"
            style={filtersActive ? clearFilterBtnActiveStyle : clearFilterBtnDisabledStyle}
            onClick={clearFilters}
            disabled={!filtersActive}
          >
            Clear
          </button>
          <button
            type="button"
            style={refreshFilterBtnStyle}
            onClick={refreshList}
            title="Refresh list"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginLeft: "auto" }}>
          <button
            type="button"
            style={filteredData.length ? excelExportBtnStyle : exportBtnDisabledStyle}
            onClick={handleExportExcel}
            disabled={!filteredData.length}
            title="Download Excel (includes resource details)"
          >
            <Sheet size={14} />
          </button>
          <button
            type="button"
            style={filteredData.length ? pdfExportBtnStyle : exportBtnDisabledStyle}
            onClick={handleExportPDF}
            disabled={!filteredData.length}
            title="Download PDF (includes resource details)"
          >
            <FileText size={14} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&display=swap');`}</style>
      <div style={{ margin: "8px", fontFamily: "'Poppins', 'Segoe UI', sans-serif" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            borderRadius: "10px",
            background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
            boxShadow: "0 2px 6px rgba(46,109,164,0.2)",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Grid3x3 size={14} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>
              Incident Category Matrix
            </div>
            <div style={{ fontSize: "10.5px", color: "rgba(255,255,255,0.75)", marginTop: "1px" }}>
              Configure disaster category, severity, and required resource quantities
            </div>
          </div>
        </div>
        <div style={{ marginTop: "10px", backgroundColor: "#fff", borderRadius: "10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
              padding: "10px 16px",
              borderRadius: "10px 10px 0 0",
              borderLeft: "4px solid #2E6DA4",
              background: "#f4f8fb",
              borderBottom: "1px solid #e5e9ef",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#1a2e35", letterSpacing: ".2px" }}>
              Active Matrix
            </span>
            <span style={{ fontSize: "10.5px", color: "#5b6b7a" }}>
              Live configurations — create, edit, view and audit history
            </span>
          </div>
          <div className="datatable__container">
            <Loader show={loader} type="body" message="Loading Data" />
            <SOPExpandableTable
              data={activeData}
              title=""
              chevronToggle={true}
              afterHeaderContent={filterBar}
              addButtonLabel={"Create Incident Category Matrix"}
              addButtonTitle={MATRIX_HELP_TEXT}
              addNewHandler={addNewHandler}
              columns={columns}
              numberOfRows={10}
              showSearch={true}
              searchPlaceholder={"Search by Incident Category, Severity"}
              searchInputStyle={{ width: "24em" }}
              showReset={false}
              rowCount={activeData.length}
              showAutoManual={false}
              showResourceQuantities={true}
              hideExpandFirstCol={true}
              headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
              tableStyle={{
                border: "1px solid #f0f0f0",
                borderTop: "none",
                borderBottom: inactiveData.length > 0 ? "none" : "1px solid #f0f0f0",
                padding: "2px",
                borderRadius: inactiveData.length > 0 ? "0" : "0 0 12px 12px",
              }}
            />
          </div>

          {inactiveData.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                  padding: "10px 16px",
                  marginTop: "18px",
                  borderLeft: "4px solid #94a3b8",
                  borderTop: "1px solid #e5e9ef",
                  borderBottom: "1px solid #e5e9ef",
                  background: "#f4f5f7",
                }}
              >
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#5b6b7a", letterSpacing: ".2px" }}>
                  Inactive Matrix
                </span>
                <span style={{ fontSize: "10.5px", color: "#8a97a3" }}>
                  Deleted configurations — view and audit history only
                </span>
              </div>
              <div className="datatable__container" style={{ paddingBottom: "12px" }}>
                <SOPExpandableTable
                  data={inactiveData}
                  title=""
                  chevronToggle={true}
                  columns={columns}
                  numberOfRows={10}
                  showSearch={false}
                  showReset={false}
                  showExport={false}
                  rowCount={inactiveData.length}
                  showAutoManual={false}
                  showResourceQuantities={true}
                  markDeletedRows={false}
                  hideExpandFirstCol={true}
                  headerTableHeadStyle={{ background: "linear-gradient(120deg, #64748b 0%, #94a3b8 100%)", color: "#FFFFFF" }}
                  tableStyle={{
                    border: "1px solid #f0f0f0",
                    borderTop: "none",
                    padding: "2px",
                    borderRadius: "0 0 12px 12px",
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <ResourceAuditHistoryModal
        show={!!historyRow}
        onClose={() => setHistoryRow(null)}
        incidentCategory={historyRow?.incident_category}
        severity={historyRow?.severity}
        groupSeqId={historyRow?.src_seq_id}
      />
    </>
  );
};

export default IncidentCategoryMatrix;
